if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.modalFooter = this.querySelector('[data-quick-add-modal-footer]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        if (this.modalFooter) this.modalFooter.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');
        if (this.modalFooter) this.modalFooter.innerHTML = '';

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.mountQuickBuyGallery(this.modalContent);
            this.initQuickBuyGallery(this.modalContent);
            this.mountQuickAddFooter();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      mountQuickBuyGallery(modalContent) {
        const template = this.querySelector('[data-quick-buy-gallery-template]');
        const mediaWrapper = modalContent?.querySelector('.product__media-wrapper');
        if (!template || !mediaWrapper) return;

        mediaWrapper.innerHTML = '';
        mediaWrapper.append(template.content.cloneNode(true));

        const slides = modalContent.querySelectorAll('.quick-buy-gallery__slide');
        const thumbs = modalContent.querySelectorAll('.quick-buy-gallery__thumb');
        console.debug('[quick-buy-gallery]', {
          slides: slides.length,
          thumbs: thumbs.length,
        });
      }

      initQuickBuyGallery(modalContent) {
        if (!modalContent) return;

        const gallery = modalContent.querySelector('[data-quick-buy-gallery]');
        if (!gallery || gallery.dataset.quickBuyGalleryBound === 'true') return;

        const track = gallery.querySelector('[data-quick-buy-gallery-track]');
        const slides = Array.from(gallery.querySelectorAll('.quick-buy-gallery__slide'));
        const thumbs = Array.from(gallery.querySelectorAll('.quick-buy-gallery__thumb'));
        if (!track || !slides.length || !thumbs.length) return;

        gallery.dataset.quickBuyGalleryBound = 'true';

        let activeIndex = 0;
        let scrollRaf = null;

        const clampIndex = (index) => Math.max(0, Math.min(slides.length - 1, index));

        const setActive = (index) => {
          activeIndex = clampIndex(index);

          slides.forEach((slide, slideIndex) => {
            const active = slideIndex === activeIndex;
            slide.classList.toggle('is-active', active);
            slide.setAttribute('aria-hidden', active ? 'false' : 'true');
          });

          thumbs.forEach((thumb, thumbIndex) => {
            const active = thumbIndex === activeIndex;
            thumb.classList.toggle('is-active', active);
            thumb.setAttribute('aria-current', active ? 'true' : 'false');
          });

          thumbs[activeIndex]?.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
          });
        };

        const updateActiveFromScroll = () => {
          scrollRaf = null;
          const slideWidth = track.clientWidth || 1;
          const index = clampIndex(Math.round(track.scrollLeft / slideWidth));
          if (index !== activeIndex) setActive(index);
        };

        setActive(0);

        thumbs.forEach((thumb, index) => {
          thumb.addEventListener('click', (event) => {
            const slide = slides[index];
            if (!slide) return;

            event.preventDefault();
            event.stopPropagation();
            setActive(index);
            slide.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'start',
            });
          });
        });

        track.addEventListener(
          'scroll',
          () => {
            if (scrollRaf) return;
            scrollRaf = requestAnimationFrame(updateActiveFromScroll);
          },
          { passive: true }
        );
      }

      mountQuickAddFooter() {
        if (!this.modalFooter || !this.modalContent) return;

        this.modalFooter.innerHTML = '';

        const stickyBar = this.modalContent.querySelector('[data-sticky-commerce-bar]');
        if (!stickyBar) return;

        this.modalFooter.append(stickyBar);

        const productForm = this.modalContent.querySelector('product-form');
        if (productForm && typeof productForm.bindStickyCommerceButton === 'function') {
          productForm.bindStickyCommerceButton();
        }
        if (productForm && typeof productForm.syncStickyCommerceButton === 'function') {
          productForm.syncStickyCommerceButton();
        }
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog:not(.product-size-guide-modal)');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
